/** 환생 도메인 부트스트래퍼 (S2b) — 환생 수행 + 환생 포인트 강화 탭. */
import { RebirthTabView } from "../ui/RebirthTabView.js";
import { RebirthTransitionOverlay } from "../ui/RebirthTransitionOverlay.js";
import { buildRebirthData } from "../ui/tabData.js";
import { t } from "../i18n/i18n.js";
import { viewTabHandle } from "@yjsgames/phaser-ui/tab-registry";
import type { DomainBootstrapper } from "./BootTypes.js";

export const rebirthBoot: DomainBootstrapper = {
  key: "rebirth",
  install(boot) {
    // 환생 버튼 경로만 전환 연출을 입힌다 — AutoPlay REBIRTH(scene.performRebirth)는 동기 직행 유지.
    const overlay = new RebirthTransitionOverlay(boot.scene);
    boot.addDisposer(() => overlay.destroy());
    boot.tabs.registerLazy("rebirth", {
      mount: () => {
        const { tabContent } = boot.layout;
        const data = (multiplier: ReturnType<RebirthTabView["currentMultiplier"]>) =>
          buildRebirthData(
            boot.ctx.rebirth(),
            boot.ctx.rebirthUpgrade(),
            boot.ctx.chapter(),
            boot.ctx.statUpgrade(),
            boot.ctx.relic(),
            multiplier,
          );
        const view = new RebirthTabView(
          boot.scene,
          tabContent.x,
          tabContent.y,
          tabContent.width,
          boot.layout.shell.tabPanelBottom,
          {
            onRebirth: () => {
              // 연출 시작 전 가드 — 불가 상태에서 연출만 재생되는 것 방지 (코어 내부 가드는 이중 안전망).
              const currentChapter = boot.ctx.chapter().currentChapter;
              const requiredChapter = boot.ctx.rebirth().requiredChapter();
              if (currentChapter < requiredChapter) {
                boot.ui.showToast(t("toast.tabLocked", { chapter: requiredChapter }));
                return;
              }
              if (overlay.isPlaying) return;
              // 비가역 리셋(골드·스탯강화·챕터·보유모듈)이라 확인 팝업을 거친다 — Yes에서만 연출+performRebirth.
              const screen = boot.screen();
              const gained = boot.ctx.rebirth().gainedPointBreakdown(
                boot.ctx.chapter().bestChapter,
                boot.ctx.rebirthUpgrade().effectOf("pointBonus"),
              ).totalPoints;
              boot.modals.openRebirthConfirm({
                screenWidth: screen.width,
                screenHeight: screen.height,
                gainedPoints: gained,
                onConfirm: () => {
                  boot.modals.closeRebirthConfirm();
                  overlay.play(() => boot.progression.performRebirth());
                },
                onCancel: () => boot.modals.closeRebirthConfirm(),
              });
            },
            onUpgradeStat: (statId) =>
              boot.progression.upgradeRebirthStat(statId, view.currentMultiplier()),
            onMultiplierChange: () => view.update(data(view.currentMultiplier())),
          },
        );
        // 시그니처(가벼운 입력 요약) — 동일하면 refresh가 update를 스킵 → buildRebirthData(composeStatCell×6) 미실행.
        // 입력: 환생강화 6종 레벨 + 스탯강화 5종 레벨(합성) + 환생포인트 + 챕터 + 유물 배수(합성).
        const R_STATS = ["attack", "fireRate", "goldGain", "critChance", "critDamage", "pointBonus"] as const;
        const S_STATS = ["attack", "fireRate", "goldGain", "critChance", "critDamage"] as const;
        const signature = (): string => {
          const ru = boot.ctx.rebirthUpgrade();
          const su = boot.ctx.statUpgrade();
          const ch = boot.ctx.chapter();
          const relic = boot.ctx.relic();
          return (
            R_STATS.map((s) => ru.level(s)).join(",") +
            "#" +
            S_STATS.map((s) => su.level(s)).join(",") +
            `#${boot.ctx.rebirth().rebirthPoint}#${ch.currentChapter},${ch.bestChapter}` +
            `#${relic.attackMultiplier()},${relic.fireRateMultiplier()},${relic.goldMultiplier()}` +
            `#${view.currentMultiplier()}`
          );
        };
        return viewTabHandle(
          view,
          () => view.update(data(view.currentMultiplier())),
          signature,
        );
      },
    });
  },
};
